import "utils/polyfills";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import Providers from "Providers";
import { TranslationProvider } from "TranslationContext";
import ErrorBoundary from "ErrorBoundary";
import { captureGlobalError } from "utils/errorCapture";
import {
  isChunkLoadError,
  isStaleChunkSyntaxError,
  reloadOnceForStaleChunk,
} from "utils/chunkReload";

// Filter out events that are NOT actionable as a frontend crash and shouldn't
// generate a FallbackUI redirect (and a polluting crcnt entry in the access log):
//   - Cross-origin "Script error." (browser hides details for security; we
//     can't act on it without source/line info).
//   - axios request timeouts (slow network / slow API; not a JS crash).
//   - axios "Network Error" (request couldn't reach the server — DNS,
//     connection refused, offline, blocked. Not a JS crash.)
//   - HTTP 401 from API (apiHelper already redirects to login; the rejection
//     races with the navigation and would otherwise surface as a crash).
const TIMEOUT_RE = /timeout exceeded|timeout of \d+ms exceeded|ECONNABORTED/i;
const STATUS_401_RE = /Request failed with status code 401/i;
const NETWORK_ERROR_RE = /^Network Error$/i;

const isNoiseError = ({ message, filename, reason } = {}) => {
  const msg = message || "";

  if (msg === "Script error." && !filename) return true;

  if (TIMEOUT_RE.test(msg)) return true;
  if (reason && TIMEOUT_RE.test(reason.message || "")) return true;
  if (reason && reason.code === "ECONNABORTED") return true;

  if (NETWORK_ERROR_RE.test(msg)) return true;
  if (reason && NETWORK_ERROR_RE.test(reason.message || "")) return true;
  if (reason && reason.code === "ERR_NETWORK") return true;

  if (reason?.response?.status === 401) return true;
  if (STATUS_401_RE.test(msg)) return true;
  if (reason && STATUS_401_RE.test(reason.message || "")) return true;

  return false;
};

// Global error capture — captures everything that escapes ErrorBoundary
// (unhandled promise rejections, async callbacks, raw window errors) and
// stashes it in sessionStorage so FallbackUI can include file/line/msg in
// the crcnt redirect URL that gets logged on the server.
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    // Stale chunk after a deploy where nginx SPA-fell-back to index.html —
    // browser parses HTML as JS and SyntaxErrors on the leading `<`. Trigger
    // a one-shot reload and short-circuit before reporting.
    if (event && isStaleChunkSyntaxError({
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
    }) && reloadOnceForStaleChunk()) {
      return;
    }
    if (isNoiseError({
      message: event && event.message,
      filename: event && event.filename,
    })) {
      return;
    }
    captureGlobalError({
      source: "window.error",
      message: event && event.message,
      filename: event && event.filename,
      lineno: event && event.lineno,
      colno: event && event.colno,
      error: event && event.error,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event && event.reason;
    // Same recovery path for the ChunkLoadError that webpack rejects with
    // when a chunk fails to register (e.g. after the SyntaxError above, or
    // a clean 404). Without this, an import() outside React.lazy would still
    // fall through to FallbackUI.
    if (isChunkLoadError(reason) && reloadOnceForStaleChunk()) {
      return;
    }
    if (isNoiseError({
      message: reason && (reason.message || String(reason)),
      reason,
    })) {
      return;
    }
    captureGlobalError({
      source: "unhandledrejection",
      message: reason && (reason.message || String(reason)),
      error: reason && reason.stack ? reason : null,
    });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Providers>
    <TranslationProvider>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </TranslationProvider>
  </Providers>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
